import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import axios from "axios";
import {
  AppCard,
  AppModal,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppTextArea,
} from "@/layout/AppComponents";
import Scrollbars from "react-custom-scrollbars";
import { Formik } from "formik";
import * as Yup from "yup";
import { axiosPath } from "../../../../../../routing/helpers/router";
import Translator from "../../../../../components/helpers/Translator";
import * as currentPageAction from "../../../../../../store/actions/currentPageAction";
import SpinnerLoader from "../../../../../../layout/components/helpers/SpinnerLoader";
import colors from "../../../../../../constants/colors";
import errorHandler from "@/layout/components/error/ErrorHandler";
import useIsMounted from "../../../../../components/hooks/useIsMounted";

export default function ReviewTab(props) {
  const { taskID } = props;
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const levels = useSelector((state) => state.currentPage?.levels);
  const level1 = useSelector((state) => state.currentPage?.levels?.level1);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const reviews = useSelector((s) => s.currentPage?.reviewTab?.[taskID]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTaskReview, setSelectedTaskReview] = useState();

  const [addReviewValidationSchema] = useState(
    Yup.object().shape({
      review: Yup.string()
        .min(0)
        .max(4)
        .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
      email: Yup.string().email(
        ReactDOMServer.renderToString(
          <Translator
            file="module-userManagement"
            name="module.um.enter-valid-email-address"
          />
        )
      ),
    })
  );

  // > LOAD REVIEWS TAB
  const loadReviews = useCallback(() => {
    setIsLoading(true);
    axios
      .post(axiosPath("api_r_user_module_correctiveActions_task_review_list"), {
        moduleBridgeHex,
        taskID,
      })
      .then((r) => {
        dispatch(
          currentPageAction.change("reviewTab", {
            [taskID]: r.data?.reviews,
          })
        );
        setIsLoading(false);
      });
  }, [moduleBridgeHex, taskID]);

  useEffect(() => {
    loadReviews();
  }, []);
  // < LOAD REVIEWS TAB

  // > EDIT REVIEW
  const handleEditReview = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_task_review_edit"),
          {
            moduleBridgeHex,
            taskID,
            taskReviewID: selectedTaskReview?.id,
            ...payload,
          }
        )
        .then((r) => {
          if (isMounted.current) {
            const updatedReviews = [...reviews];
            updatedReviews[selectedTaskReview.pos] = {
              ...updatedReviews[selectedTaskReview.pos],
              value: r.data?.review?.value,
              message: r.data?.review?.message,
            };

            dispatch(
              currentPageAction.change("reviewTab", {
                [taskID]: updatedReviews,
              })
            );

            dispatch(
              currentPageAction.change("levels", {
                ...levels,
                level1: {
                  ...level1,
                  [taskID]: {
                    ...level1?.[taskID],
                    reviewValue: r.data?.taskReviewValue,
                  },
                },
              })
            );

            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="module-correctiveActions" name="completed" />
              ),
              message: ReactDOMServer.renderToString(
                <Translator
                  file="module-correctiveActions"
                  name="edited.review"
                />
              ),
              type: "success",
              duration: 3000,
              position: "top-right",
            });
            setSelectedTaskReview(undefined);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID, selectedTaskReview]
  );
  // < EDIT REVIEW

  // > DELETE REVIEW
  const handleDeleteReview = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_task_review_delete"),
          {
            moduleBridgeHex,
            taskID,
            taskReviewID: selectedTaskReview?.id,
          }
        )
        .then((r) => {
          if (isMounted.current) {
            const updatedReviews = reviews.filter(
              (review) => review.id !== selectedTaskReview.id
            );

            dispatch(
              currentPageAction.change("reviewTab", {
                [taskID]: [...updatedReviews],
              })
            );

            dispatch(
              currentPageAction.change("levels", {
                ...levels,
                level1: {
                  ...level1,
                  [taskID]: {
                    ...level1?.[taskID],
                    reviewValue: r.data?.taskReviewValue,
                    reviewCount: r.data?.reviewCount,
                  },
                },
              })
            );

            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="module-correctiveActions" name="completed" />
              ),
              message: ReactDOMServer.renderToString(
                <Translator
                  file="module-correctiveActions"
                  name="deleted.review"
                />
              ),
              type: "success",
              duration: 3000,
              position: "top-right",
            });
            setSelectedTaskReview(undefined);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID, selectedTaskReview]
  );
  // < DELETE REVIEW

  const renderReviewStars = (value) => {
    let reviewColor;

    switch (value) {
      case 1:
      case 2:
        reviewColor = colors.danger;
        break;
      case 3:
        reviewColor = colors.state2;
        break;
      case 4:
      case 5:
        reviewColor = colors.success;
        break;
      default:
        reviewColor = colors.grey400;
    }

    return Array.from({ length: 5 }, (_, i) => (
      <AppRemixIcon
        key={i}
        icon="star"
        color={value >= i + 1 ? reviewColor : colors.grey400}
        filled={value >= i + 1}
      />
    ));
  };

  const renderEditReviewStars = (setFieldValue, values) => {
    let reviewColor;

    switch (values?.review) {
      case 1:
      case 2:
        reviewColor = colors.danger;
        break;
      case 3:
        reviewColor = colors.state2;
        break;
      case 4:
      case 5:
        reviewColor = colors.success;
        break;
      default:
        reviewColor = colors.grey400;
    }

    return Array.from({ length: 5 }, (_, i) => (
      <ReviewStar
        key={i}
        icon="star"
        size={22}
        color={values?.review >= i + 1 ? reviewColor : colors.grey400}
        filled={values?.review >= i + 1}
        onClick={() => setFieldValue("review", i + 1)}
      />
    ));
  };

  return (
    <Container>
      {isLoading ? (
        <AppNoInfo color={colors.primaryLight}>
          <SpinnerLoader />
        </AppNoInfo>
      ) : (
        <>
          {reviews?.length > 0 && (
            <Scrollbars style={{ height: "360px" }}>
              {reviews?.map((r, index) => (
                <AppCard key={r?.id} className="mb-2">
                  <div className="d-flex justify-content-between">
                    <div>
                      <div>{renderReviewStars(r?.value)}</div>
                      <div>{r?.message}</div>
                    </div>
                    <div className="d-flex flex-column align-items-end">
                      <div>
                        {r?.userBridge
                          ? r?.userBridge?.name || r?.userBridge?.user
                          : r?.email}
                      </div>
                      <div>
                        <small>{r?.date}</small>
                      </div>
                      {(r?.userBridge?.id === userBridge?.id ||
                        (!r?.userBridge && r?.email)) &&
                        userBridge?.permissions?.level !== 2 && (
                          <div
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-correctiveActions"
                                name="edit"
                              />
                            )}
                            className="pointer"
                            onClick={() =>
                              setSelectedTaskReview({
                                id: r?.id,
                                pos: index,
                              })
                            }
                          >
                            <AppRemixIcon
                              icon="edit-box"
                              color={colors.primary}
                            />
                          </div>
                        )}
                    </div>
                  </div>
                </AppCard>
              ))}
            </Scrollbars>
          )}
          <AppModal
            show={!!selectedTaskReview}
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-correctiveActions"
                name="initiative.review"
              />
            )}
            onClose={() => setSelectedTaskReview(undefined)}
            size="lg"
          >
            <Formik
              initialValues={{
                review: reviews[selectedTaskReview?.pos]?.value,
                message: reviews[selectedTaskReview?.pos]?.message,
              }}
              validationSchema={addReviewValidationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={handleEditReview}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div>
                    <label>Valoración</label>
                    <div className="d-flex">
                      {renderEditReviewStars(setFieldValue, values)}
                    </div>
                    {errors?.review && (
                      <div className="text-danger">*{errors?.review}</div>
                    )}
                  </div>
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-correctiveActions"
                        name="leave.your.comments"
                      />
                    )}
                    icon={<AppRemixIcon icon="chat-4" />}
                    onChange={(opt) => setFieldValue("message", opt)}
                    value={values?.message}
                    error={errors?.message}
                  />
                  <div className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleDeleteReview}
                    >
                      <AppRemixIcon
                        icon="delete-bin-6"
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-correctiveActions"
                            name="delete"
                          />
                        )}
                      />
                    </button>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-light mr-2"
                        onClick={() => setSelectedTaskReview(undefined)}
                      >
                        <Translator
                          file="module-correctiveActions"
                          name="cancel"
                        />
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        <Translator
                          file="module-correctiveActions"
                          name="edit.review"
                        />
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </AppModal>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  height: 400px;
  overflow: auto;
`;

const ReviewStar = styled(AppRemixIcon)`
  cursor: pointer;
  margin-left: 5px;
  margin-right: 5px;
`;
